/*
 * ------------------------------------------------------------
 * TEMPORARY: cooker launch PDP. Remove after 30 Nov 2026 (see snippets/pc-cooker-head.liquid).
 * Behaviour for sections/pc-buy-bar.liquid.
 *
 * - Points Buy Now and Add to Cart at the main product form (form="..."), so submitting runs the theme's own
 *   <product-form> add-to-cart (theme.js ProductForm): AJAX add, spinner on the loader-button, mini-cart, errors.
 * - Buy Now goes to checkout once the theme's "variant:added" event confirms the add succeeded.
 * - Before adding, if the variant options are still below the screen (shopper hasn't reached them), scrolls them
 *   into view instead, so a finish and size get picked first. This check runs from a window click listener that
 *   layout/theme.liquid registers ahead of GoKwik's (snippets/gokwik.liquid): GoKwik takes over any "Buy Now"
 *   button from its own window listener, so a check on the bar itself would never run for Buy Now. When the
 *   options are reached, the click goes on as usual: GoKwik's checkout for Buy Now, the theme's add for Add to Cart.
 * - Tapping the summary (title and price) scrolls to the options whenever they are off screen, up or down.
 *   It never adds to cart.
 * - Follows the theme's "variant:changed" event to keep the price, compare-at price and sold-out state in sync.
 * - Launch timer strip (optional): counts down to its data-end-date as "06d 08h 30m 25s", once a second; hidden when the
 *   date is invalid or has passed.
 * - Publishes its height as --pc-buy-bar-height (floating buttons above the bar) and --cart-notification-offset (theme notifications
 *   sit above it, as they do above the theme's own sticky form).
 *
 * Reuses from theme.js: formatMoney(), loader-button, product-form, variant:changed, variant:added.
 * ------------------------------------------------------------
 */
(() => {
  if (window.customElements.get("pc-buy-bar")) {
    return;
  }

  /* The options count as "reached" once their top is this far above the bottom bar */
  const REACHED_MARGIN = 40;

  class PcBuyBar extends HTMLElement {
    connectedCallback() {
      this.priceElement = this.querySelector("[data-pc-variant-price]");
      this.comparePriceElement = this.querySelector("[data-pc-compare-price]");
      this.titleElement = this.querySelector("[data-pc-title]");
      this.finishesElement = this.querySelector("[data-pc-finishes]");
      this.buyNowPending = false;

      if (!this._linkForm()) {
        this.buttons.forEach((button) => button.disabled = true);
      }

      this._onAtcClick = this._onAtcClick.bind(this);
      this._guardClick = this._guardClick.bind(this);

      /* Normally layout/theme.liquid's early listener calls this; without it (bar on another template) listen here */
      window.pcBuyBarGuard = this._guardClick;

      if (!window.pcBuyBarEarlyClick) {
        window.addEventListener("click", this._guardClick, true);
      }
      this._onVariantChanged = this._onVariantChanged.bind(this);
      this._onVariantAdded = this._onVariantAdded.bind(this);
      this._onProductChanged = this._onProductChanged.bind(this);

      this.addEventListener("click", this._onAtcClick, true);
      document.addEventListener("variant:changed", this._onVariantChanged);
      document.addEventListener("variant:added", this._onVariantAdded);
      document.addEventListener("product:changed", this._onProductChanged);

      this._startTimer();

      this.resizeObserver = new ResizeObserver(() => this._publishHeight());
      this.resizeObserver.observe(this);
      this._publishHeight();
    }

    disconnectedCallback() {
      window.removeEventListener("click", this._guardClick, true);

      if (window.pcBuyBarGuard === this._guardClick) {
        window.pcBuyBarGuard = null;
      }

      document.removeEventListener("variant:changed", this._onVariantChanged);
      document.removeEventListener("variant:added", this._onVariantAdded);
      document.removeEventListener("product:changed", this._onProductChanged);
      this.resizeObserver.disconnect();
      clearInterval(this.timerInterval);
      document.documentElement.style.removeProperty("--pc-buy-bar-height");
      document.documentElement.style.removeProperty("--cart-notification-offset");
    }

    /* Queried live: GoKwik replaces the Buy Now button with a copy of itself, so a stored reference goes stale */
    get buttons() {
      return Array.from(this.querySelectorAll("[data-pc-atc]"));
    }

    /*
     * Points the buttons at the main product form. Looked up again on every click: the size swatches swap the
     * product in place (custom.js), which replaces the form and changes its id.
     *
     * getAttribute("id"), never form.id: the form holds an <input name="id"> (the variant), and a form's named
     * controls override its properties, so form.id is that input, not the form's id (theme.js ProductForm relies
     * on this with this.id.disabled).
     */
    _linkForm() {
      this.form = document.querySelector('form[id^="product-form-main-"]');

      if (this.form) {
        this.buttons.forEach((button) => button.setAttribute("form", this.form.getAttribute("id")));
      }

      return this.form;
    }

    _startTimer() {
      this.timerElement = this.querySelector("[data-pc-timer]");

      if (!this.timerElement) {
        return;
      }

      this.timerEnd = Date.parse(this.timerElement.dataset.endDate);
      this.timerValueElement = this.timerElement.querySelector("[data-pc-timer-value]");

      if (this._tickTimer()) {
        this.timerElement.hidden = false;
        this.timerInterval = setInterval(() => this._tickTimer(), 1000);
      }
    }

    /* Returns false (and hides the strip) once the end date has passed, or when it isn't a valid date */
    _tickTimer() {
      const remaining = Math.floor((this.timerEnd - Date.now()) / 1000);

      if (!(remaining > 0)) {
        clearInterval(this.timerInterval);
        this.timerElement.hidden = true;
        return false;
      }

      const pad = (value) => String(value).padStart(2, "0");
      const days = Math.floor(remaining / 86400);
      const hours = Math.floor(remaining % 86400 / 3600);
      const minutes = Math.floor(remaining % 3600 / 60);

      this.timerValueElement.textContent = `${pad(days)}d ${pad(hours)}h ${pad(minutes)}m ${pad(remaining % 60)}s`;
      return true;
    }

    _publishHeight() {
      document.documentElement.style.setProperty("--pc-buy-bar-height", `${this.offsetHeight}px`);
      document.documentElement.style.setProperty("--cart-notification-offset", `${this.offsetHeight}px`);
    }

    /* The first variant option block in the main product info: colour/size picker or the cross-product swatches */
    get optionsElement() {
      const candidates = Array.from(document.querySelectorAll(".product__info .product-form__option-selector, .product__info > .product-swatch"));

      return candidates
        .filter((element) => element.offsetParent !== null)
        .sort((a, b) => a.getBoundingClientRect().top - b.getBoundingClientRect().top)[0];
    }

    /* Runs first on every click on the page (window, capture phase); stops a bar button's click and scrolls when the
       shopper hasn't reached the options yet. stopImmediatePropagation keeps GoKwik's listener, registered after it
       on window, from seeing the click. */
    _guardClick(event) {
      const button = event.target.closest && event.target.closest("[data-pc-atc]");

      if (!button || !this.contains(button)) {
        return;
      }

      const options = this.optionsElement;

      if (!options) {
        return; // nothing to choose, add straight away
      }

      if (options.getBoundingClientRect().top > window.innerHeight - this.offsetHeight - REACHED_MARGIN) {
        event.preventDefault();
        event.stopImmediatePropagation();
        this._scrollToOptions(options);
      }
    }

    /* Only reached by clicks _guardClick let through */
    _onAtcClick(event) {
      const button = event.target.closest("[data-pc-atc]");

      if (!button) {
        if (event.target.closest(".pc-buy-bar__summary")) {
          this._onSummaryClick();
        }

        return;
      }

      /* Set on every click, so a failed Buy Now never sends a later Add to Cart to checkout */
      this.buyNowPending = button.hasAttribute("data-pc-buy-now");
      this._linkForm();
    }

    /* Scrolls only when the options are off screen: below the bar, or above the top (shopper scrolled past them) */
    _onSummaryClick() {
      const options = this.optionsElement;

      if (!options) {
        return;
      }

      const top = options.getBoundingClientRect().top;

      if (top < 0 || top > window.innerHeight - this.offsetHeight - REACHED_MARGIN) {
        this._scrollToOptions(options);
      }
    }

    _scrollToOptions(options) {
      /* Header height, not getStickyHeaderOffset(): that is 0 while assets/pc-cooker.js hides the header, but
         scrolling up brings the header back, so room for it is always needed */
      const headerHeight = parseInt(getComputedStyle(document.documentElement).getPropertyValue("--header-height")) || 0;
      const top = options.getBoundingClientRect().top + window.scrollY - headerHeight - 24;
      window.scrollTo({ top, behavior: "smooth" });
    }

    /* The theme's events bubble up from the form itself; read its id attribute (see _linkForm for why not .id) */
    _isMainForm(target) {
      const formId = target.getAttribute && target.getAttribute("id");
      return Boolean(formId && formId.startsWith("product-form-main-"));
    }

    /* Fired by ProductForm only after a successful add, so failed adds stay on the page with the theme's error */
    _onVariantAdded(event) {
      if (!this.buyNowPending || !this._isMainForm(event.target)) {
        return;
      }

      this.buyNowPending = false;
      window.location.href = `${window.Shopify.routes.root}checkout`;
    }

    _onVariantChanged(event) {
      if (this._isMainForm(event.target)) {
        this._applyVariant(event.detail.variant);
      }
    }

    /*
     * The size swatches (<product-swatch>, assets/custom.js) swap in another product's main section and send
     * "product:changed", but no "variant:changed" (and a single-variant product has no variant picker to send one).
     * So read the variant the new form adds and fetch its product's data, once per swap, to update the bar (title and
     * finish count included, as the swapped-in product has its own).
     */
    async _onProductChanged(event) {
      const productUrl = event.detail?.productUrl;
      const variantId = Number(this._linkForm()?.querySelector('[name="id"]')?.value);

      if (!productUrl || !variantId) {
        return;
      }

      try {
        const product = await (await fetch(`${productUrl.split("?")[0]}.js`)).json();
        this._applyProduct(product);
        this._applyVariant(product.variants.find((variant) => variant.id === variantId));
      } catch (error) {
        // Keeps the previous product's state; the next variant change or page load corrects it
      }
    }

    /* Title and finish count; same option lookup as sections/pc-buy-bar.liquid */
    _applyProduct(product) {
      if (this.titleElement) {
        this.titleElement.textContent = product.title;
      }

      if (this.finishesElement) {
        const finishOption = product.options.find((option) => ["finish", "color", "colour"].includes(option.name.toLowerCase())) || product.options[0];
        const count = finishOption ? finishOption.values.length : 0;

        this.finishesElement.textContent = this.finishesElement.dataset.text.replace("[count]", count);
        this.finishesElement.hidden = count < 2;
      }
    }

    /* Buttons, price and compare-at price for the selected variant (null: no valid variant, disable) */
    _applyVariant(variant) {
      this.buttons.forEach((button) => {
        const label = button.querySelector(".loader-button__text") || button;
        button.disabled = !variant || !variant.available;

        if (variant) {
          label.textContent = variant.available ? button.dataset.label : this.dataset.soldOutLabel;
        }
      });

      if (variant) {
        const format = (cents) => formatMoney(cents).replace(/[.,]00(?!\d)/, "");
        const onSale = variant.compare_at_price > variant.price;

        /* innerHTML: the shop's money format wraps the amount in <span class=money> */
        if (this.priceElement) {
          this.priceElement.innerHTML = format(variant.price);
        }

        if (this.comparePriceElement) {
          this.comparePriceElement.innerHTML = onSale ? format(variant.compare_at_price) : "";
          this.comparePriceElement.hidden = !onSale;
        }
      }
    }
  }

  window.customElements.define("pc-buy-bar", PcBuyBar);
})();
